import { defineStore } from "pinia"
import { useDYFetch } from "../composables/fetch"

export interface ShortCutMenu {
  "title": string
  "icon": string
  "actions": [
    {
      "methodName": "METHOD_GO_TO_URL" | "METHOD_GO_TO_ROUTER",
      "methodParams": [
        string
      ]
      "methodStyle": string
      "platforms": number
    }
  ],
  "showAsAction": string
  "image": string | null
  "text": string
  "shortcutNavigation": {
    description: string,
    image: string,
    list: [any]
    // specialData: [any],
    specialData: SpecialShortCut
    specialType: string
  }
}

export type SpecialShortCut = {data:Record<string,specialData[]>, img?:string}[]
export type SpecialShortCutTour = {data:Record<string,specialData[]>, img?:string}

export interface specialData {
  board: string
  lect_mode: string
  lecture: string
  lectureName: string
  new_icon: string
  no: number
  qna_open: string
  showTitle: string
  teacher: string
}

export const useDYMainStore = defineStore('nuxt3-module-doyac-main', () => {
  const isLanding = ref(true)
  const isLoading = ref(false)
  const isRouteChanging = ref(false)
  const shortCutJson = ref('')
  const bannerJson = ref('')
  const actionbarTitle = ref('')
  const lastScrollY = ref(0)
  const bodyData = ref()
  const inisisResultData = ref()
  // "앱으로 열기" 버튼이 열 인앱 대상 경로 오버라이드(/v 제외, 예: '/guide/123').
  // 특정 페이지(지도 전용/샘플 플레이어 등)에서 현재 경로 대신 해당 콘텐츠 페이지를 앱에서 열고 싶을 때 설정한다.
  // 페이지 진입 시 설정하고 이탈 시 null 로 되돌린다. 미설정(null)이면 기존대로 현재 경로를 사용한다.
  const appOpenTargetPath = ref<string | null>(null)
  // "앱으로 열기" 버튼이 열 인앱 네이티브 딥링크 경로 오버라이드(예: '/payments/courses/sample/tg123').
  // 설정되면 링크가 기존 webUrl(웹 URL) 방식 대신 appUrl(앱 네이티브 경로) 방식으로 만들어진다.
  // 특정 페이지(투어 플레이어 등)에서 웹 페이지가 아니라 앱의 특정 화면으로 바로 열고 싶을 때 설정한다.
  // 페이지 진입 시 설정하고 이탈 시 null 로 되돌린다. 미설정(null)이면 기존대로 webUrl 방식을 사용한다.
  const appOpenAppUrl = ref<string | null>(null)

  const isSSR = computed(() => import.meta.server)

  // 캐시 및 인플라이트 프라미스로 중복 요청 방지
  const shortCutDataCache = ref<ShortCutMenu[] | undefined>()
  let shortCutInFlight: Promise<ShortCutMenu[] | undefined> | undefined

  /**
   * prepare.store-server.ts 에서 요청한 숏컷 데이터를 받아오는 함수
   * @returns 
   */
  const getShortCut = async (): Promise<ShortCutMenu[] | undefined> => {
    // 플러그인에서 URL 세팅 전이면 요청하지 않음
    if (!shortCutJson.value) {
      return shortCutDataCache.value
    }

    if (shortCutDataCache.value && shortCutDataCache.value.length > 0) {
      return shortCutDataCache.value
    }

    if (shortCutInFlight) {
      return shortCutInFlight
    }

    const response = useDYFetch<ShortCutMenu[]>(shortCutJson.value, { immediate: false })

    shortCutInFlight = (async () => {
      try {
        await response.execute({ dedupe: 'defer' })
        shortCutDataCache.value = (response.data.value ?? undefined)
        return shortCutDataCache.value
      } finally {
        shortCutInFlight = undefined
      }
    })()

    return shortCutInFlight
  }

  return {
    /**
     * 랜딩 상태 식별값. 이 값이 `true`라면 앱이 서버에서 기본 데이터를 불러들이는 작업 등을 진행 중이라는 뜻이다.
     */
    isLanding,
    /**
     * 전역 로딩 상태 식별값. 이 값이 `true`면 오버레이 형태의 로딩 UI가 노출될 수 있음을 뜻한다.
     */
    isLoading,
    /**
     * 라우트가 변하고 있는지 식별하는 값. `DYSwiper` 등의 컴퍼넌트가 사용함.
     */
    isRouteChanging,
    bannerJson,
    /**
     * 초기 외부에서 할당함
     */
    shortCutJson,
    /**
     * 상단 액션바 타이틀. 일정 width이하인 경우에만 노출
     */
    actionbarTitle,
    lastScrollY,
    bodyData,
    /**
     * 이니시스 결제 결과 데이터로 우선 쓸 예정
     */
    inisisResultData,
    /**
     * "앱으로 열기" 버튼이 열 인앱 대상 경로 오버라이드(/v 제외). 미설정이면 현재 경로 사용.
     */
    appOpenTargetPath,
    /**
     * "앱으로 열기" 버튼이 열 인앱 네이티브 딥링크 경로 오버라이드. 설정되면 webUrl 대신 appUrl 방식으로 링크를 만든다.
     */
    appOpenAppUrl,
    isSSR,
    shortCutDataCache,
    getShortCut
  }
})